import { X } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@krea/shared/utils";

import { useIsKeyboardOpen } from "../../hooks/useIsKeyboardOpen";
import { useMobile } from "../../hooks/useMobile";
import { Button } from "../Button";
import { Drawer, DrawerClose, DrawerContent } from "../Drawer";
import * as Base from "./base-modal";

interface IHeaderProps {
  title: string;
  hideCloseButton?: boolean;
}

const CloseElement = ({ className }: { className?: string }) => (
  <Base.ResponsiveDialogClose asChild>
    <Button
      variant="ghost"
      size="icon"
      aria-label="Close"
      className={cn("absolute right-3 size-11 md:size-10", className)}
    >
      <X className="size-6" strokeWidth={2} />
      <span className="sr-only">Close</span>
    </Button>
  </Base.ResponsiveDialogClose>
);

const MobileCloseElement = ({ className }: { className?: string }) => (
  <DrawerClose asChild>
    <Button
      variant="ghost"
      size="icon"
      aria-label="Close"
      className={cn("absolute right-3 size-11 md:size-10", className)}
    >
      <X className="size-6" strokeWidth={2} />
      <span className="sr-only">Close</span>
    </Button>
  </DrawerClose>
);

const Header = ({
  title,
  hideCloseButton,
  mobile = false,
}: IHeaderProps & { mobile?: boolean }) => {
  const CloseBtn = mobile ? MobileCloseElement : CloseElement;
  return (
    <div
      className={cn(
        // relative so the absolute-positioned close button anchors to the
        // header (scrolls with it) instead of floating over the panel
        "relative flex w-full items-center justify-center gap-2 rounded-t-xl border-0 border-b border-solid border-gray-200 px-2 py-3",
        // min-h, not h: a title that wraps to a second line grows the header
        // instead of overflowing it.
        !hideCloseButton && "min-h-12",
      )}
      id="dialog-header"
    >
      {/* The close button is absolutely positioned, so it takes no space in
          this row. Reserve its footprint (right-3 + size-11 = 56px) on BOTH
          sides, so the title stays genuinely centred AND can never run
          underneath the button. */}
      <div
        className={cn("flex-1 text-center", hideCloseButton ? "px-6" : "px-14")}
      >
        <h5 className="my-0">{title}</h5>
      </div>

      {!hideCloseButton && <CloseBtn />}
    </div>
  );
};

interface IResponsiveDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  children: ReactNode;

  onClose?: () => void;
  title?: string;
  lockOutsideInteraction?: boolean;
  className?: string;
  dialogClassName?: string;
  footer?: ReactNode;
  hideCloseButton?: boolean;
  /** Mobile only. vaul snap points, e.g. [0.5, 1] to allow dragging up to full height. */
  snapPoints?: (number | string)[];
}

export const AppModal = ({
  open,
  setOpen,
  children,
  onClose,
  title,
  lockOutsideInteraction = true,
  className,
  dialogClassName,
  footer,
  hideCloseButton = false,
  snapPoints,
}: IResponsiveDialogProps) => {
  const isKeyboardOpen = useIsKeyboardOpen(open);
  const isMobileDevice = useMobile();

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setOpen(nextOpen);
    } else {
      onClose?.();
      setOpen(nextOpen);
    }
  };

  if (isMobileDevice) {
    return (
      // repositionInputs=false disables vaul's built-in keyboard-avoidance jump; the
      // existing useIsKeyboardOpen hook handles keyboard state instead.
      // Note: vaul v1.x dismissible={false} blocks ALL close mechanisms (including X button),
      // so we don't use it. lockOutsideInteraction is enforced on desktop via onInteractOutside.
      <Drawer
        open={open}
        onOpenChange={handleOpenChange}
        repositionInputs={false}
        snapPoints={snapPoints}
      >
        <DrawerContent
          hideCloseButton
          contentClassName=""
          className={cn("z-400001", dialogClassName)}
          overlayClassName="z-400000"
        >
          {title && (
            <Header title={title} hideCloseButton={hideCloseButton} mobile />
          )}
          {/* Scrolling is owned by DrawerContent's wrapper — no nested scroll
              container here, so sticky footers inside children stick to the
              drawer's visible bottom edge (flush with the keyboard when open). */}
          <div
            className={cn(
              "w-full pt-4 px-2",
              isKeyboardOpen ? "pb-0" : "pb-8",
              className,
            )}
          >
            {children}
            {footer && (
              <Base.ResponsiveDialogFooter className="mb-6 mt-3 w-full">
                {footer}
              </Base.ResponsiveDialogFooter>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Base.ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      <Base.ResponsiveDialogContent
        className={cn(
          // AppModal hides the default top-right close button and renders its
          // own header-based one, so undo the bottom-sheet variant's reserved
          // right padding (max-lg:pr-14) that would otherwise offset content.
          "m-0 py-0 max-lg:pr-6 [&_button#-default-dialog-close-icon]:hidden",
          dialogClassName,
        )}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (lockOutsideInteraction) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (lockOutsideInteraction) e.preventDefault();
        }}
        id="dialog-content"
      >
        {title && (
          <Base.ResponsiveDialogTitle>
            <Header title={title} hideCloseButton={hideCloseButton} />
          </Base.ResponsiveDialogTitle>
        )}
        <div
          className={cn(
            "w-full pt-0 pb-4",
            "[&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-3xl ",
            "max-h-full",
            {
              "min-h-[200px] max-h-[400px] pb-[30px]": isKeyboardOpen,
            },
            className,
          )}
        >
          {children}
          {footer && (
            <Base.ResponsiveDialogFooter className="mb-6 mt-3 w-full">
              {footer}
            </Base.ResponsiveDialogFooter>
          )}
        </div>
      </Base.ResponsiveDialogContent>
    </Base.ResponsiveDialog>
  );
};
